import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import type { IconNames } from '@cobliteam/ui/icons';
import { stripHighlightMarkup } from './RiskHighlightText';

// `metadata.rule_name` da detecção principal (a MESMA que vira o headline/fator principal) → ícone do tipo
// de risco. Fonte única compartilhada entre o card flutuante e o sino do feed (NotificationFeedButton), pra ícone
// e título nunca divergirem. O `source.key` (workflow disparado) NÃO serve: pode diferir por agregação
// cross-rule (ex: workflow de fadiga cujo risco dominante é jornada).
export const RISK_RULE_ICON: Record<string, IconNames> = {
  fatigue: 'coffee',
  long_journey: 'clock',
  severe_speed: 'gauge',
};

export const FALLBACK_RISK_ICON: IconNames = 'message-square-warning';

// Ícone do risco a partir da regra da detecção principal; cai no genérico quando não há regra mapeada.
export const getRiskRecognitionIcon = (highlight: RiskRecognitionHighlight): IconNames => {
  const ruleName = highlight.metadata?.rule_name;
  return (ruleName ? RISK_RULE_ICON[ruleName] : undefined) ?? FALLBACK_RISK_ICON;
};

export interface RiskRecognitionCardDisplay {
  headline: string;
  subject: string | null;
  summary: string;
  location: string | null;
  description: string;
  recommendation: string | null;
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const removeLeadingSubject = (text: string, driverName?: string, vehiclePlate?: string): string => {
  let result = text.trim();
  if (driverName) {
    const pattern = new RegExp(`^${escapeRegExp(stripHighlightMarkup(driverName))}\\s*`, 'i');
    result = result.replace(pattern, '');
  }
  if (vehiclePlate) {
    result = result.replace(new RegExp(`\\(\\s*${escapeRegExp(vehiclePlate)}\\s*\\)`, 'i'), '');
  }
  return result.replace(/^\s*registra\s+/i, '').trim();
};

const shortenAddress = (address: string): string => {
  const trimmed = address.trim();
  if (!trimmed) return '';

  const parts = trimmed
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    const cityPart = parts.find((part) => part.includes(' - ')) ?? parts.at(-1) ?? trimmed;
    return cityPart.replace(/\s*-\s*$/, '').trim();
  }

  return trimmed.length > 48 ? `${trimmed.slice(0, 45)}…` : trimmed;
};

const buildSubject = (highlight: RiskRecognitionHighlight): string | null => {
  const metadata = highlight.metadata;
  if (!metadata) return null;

  const parts = [metadata.driver_name, metadata.vehicle_plate || metadata.vehicle_alias].filter((part): part is string => Boolean(part?.trim()));
  return parts.length > 0 ? parts.join(' · ') : null;
};

// Monta cópia do card/toast sem repetir motorista no título e na descrição: fator principal + metadados + local.
export const formatRiskRecognitionCardDisplay = (highlight: RiskRecognitionHighlight): RiskRecognitionCardDisplay => {
  const metadata = highlight.metadata;
  const riskFactors = highlight.risk_factors ?? [];
  const mainFactor = riskFactors.find((factor) => factor.type === 'main') ?? riskFactors[0];

  const headline = mainFactor?.title ? `<bold>${mainFactor.title}</bold>` : removeLeadingSubject(stripHighlightMarkup(highlight.title), metadata?.driver_name) || highlight.title;

  const summary =
    mainFactor?.description ?? (removeLeadingSubject(stripHighlightMarkup(highlight.description), metadata?.driver_name, metadata?.vehicle_plate) || highlight.description);

  const rawAddress = metadata?.last_location?.address;
  const location = rawAddress ? shortenAddress(rawAddress) : null;
  const recommendation = (highlight.suggestions ?? []).find((suggestion) => suggestion.trim().length > 0)?.trim() ?? null;

  return {
    headline,
    subject: buildSubject(highlight),
    summary,
    location,
    description: highlight.description,
    recommendation,
  };
};
